import { SVGGSelection, SVGSVGSelection } from 'components/Charting/types';
import { ScaleLinear, scaleLinear, ScaleTime, scaleTime } from 'd3-scale';
import { select } from 'd3-selection';

export type XDomain = Date[];
export type YDomain = number[];
export type XScale = ScaleTime<number, number>;
export type YScale = ScaleLinear<number, number, never>;

export interface ChartConfig {
  chartName: string;
  margin: { left: number; top: number; bottom: number; right: number };
}

export interface ChartContext<TDataContext> {
  config: ChartConfig;
  chartContainerElement: HTMLDivElement;
  svg: SVGSVGSelection;
  chartG: SVGGSelection;
  xAxisGridLinesG: SVGGSelection;
  yAxisGridLinesG: SVGGSelection;
  xAxisBottomG: SVGGSelection;
  yAxisLeftG: SVGGSelection;
  getInnerDimensions: (
    props: Pick<ChartContext<TDataContext>, 'config' | 'svg'>,
  ) => {
    innerWidth: number;
    innerHeight: number;
  };
  getXScale: (props: { innerWidth: number; xDomain: XDomain }) => XScale;
  getYScale: (props: { innerHeight: number; yDomain: YDomain }) => YScale;

  update: (data: TDataContext | undefined, forceRedraw?: boolean) => void;
  destroy: () => void;
}

export type ChartBuilder<
  TDataContext,
  TChartContext extends ChartContext<TDataContext>,
> = (
  config: TChartContext['config'],
  chartContainerElement: HTMLDivElement,
) => TChartContext;

const getSvgAndGs = (
  config: ChartConfig,
  chartContainerElement: HTMLDivElement,
) => {
  const svg = select(chartContainerElement)
    .append('svg')
    .classed(`${config.chartName}-svg`, true)
    .lower()
    .attr('width', '100%')
    .attr('height', '100%');

  const chartG = svg.append('g').classed('chart', true);

  chartG.attr(
    'transform',
    `translate(${config.margin.left}, ${config.margin.top})`,
  );

  const xAxisGridLinesG = chartG.append('g');
  const xAxisBottomG = chartG.append('g');
  const yAxisGridLinesG = chartG.append('g');
  const yAxisLeftG = chartG.append('g');

  return {
    svg,
    chartG,
    xAxisGridLinesG,
    yAxisGridLinesG,
    xAxisBottomG,
    yAxisLeftG,
  };
};

const getInnerDimensions = <TDataContext>({
  config,
  svg,
}: Pick<ChartContext<TDataContext>, 'config' | 'svg'>) => {
  const getBoundingDimensions = (element: SVGSVGElement) => {
    const parentElement = element.parentNode as Element | null;
    if (!parentElement) {
      throw Error('element has no parent element');
    }

    const { width, height } = parentElement.getBoundingClientRect();

    return { width, height };
  };

  const { width, height } = getBoundingDimensions(svg.node()!);

  return {
    innerWidth: width - (config.margin.left + config.margin.right),

    innerHeight: height - (config.margin.top + config.margin.bottom),
  };
};

const getXScale = ({
  innerWidth,
  xDomain,
}: {
  innerWidth: number;
  xDomain: XDomain;
}) => scaleTime().domain(xDomain).range([0, innerWidth]);

const getYScale = ({
  innerHeight,
  yDomain,
}: {
  innerHeight: number;
  yDomain: YDomain;
}) => scaleLinear().domain(yDomain).range([innerHeight, 0]);

const onUpdate = () => {};
const onDestroy = () => {};

export const baseChartBuilder = <TDataContext>(
  config: ChartConfig,
  chartContainerElement: HTMLDivElement,
): ChartContext<TDataContext> => {
  const {
    svg,
    chartG,
    xAxisGridLinesG,
    yAxisGridLinesG,
    xAxisBottomG,
    yAxisLeftG,
  } = getSvgAndGs(config, chartContainerElement);

  return {
    config,
    chartContainerElement,

    svg,
    chartG,
    xAxisGridLinesG,
    yAxisGridLinesG,
    xAxisBottomG,
    yAxisLeftG,

    getInnerDimensions,
    getXScale,
    getYScale,

    update: onUpdate,
    destroy: onDestroy,
  };
};
